<template>
    <div class="product-category">
      <div class="page-header">
        <div>
          <h2 class="page-title">商品分类</h2>
          <p class="page-desc">管理商品分类，方便用户快速查找</p>
        </div>
        <el-button type="primary" size="large" @click="openAddDialog">
          <el-icon><Plus /></el-icon>
          新增分类
        </el-button>
      </div>
      
      <!-- 分类列表 -->
      <div class="category-list">
        <div v-for="category in categories" :key="category.id" class="category-item">
          <div class="category-info">
            <div class="category-icon">
              <el-icon :size="32" color="#409eff"><Folder /></el-icon>
            </div>
            <div class="category-detail">
              <div class="category-name">{{ category.category_name }}</div>
              <div class="category-meta">
                <span>排序: {{ category.sort_order }}</span>
                <span>状态: 
                  <el-tag :type="category.status === 1 ? 'success' : 'danger'" size="small">
                    {{ category.status === 1 ? '启用' : '禁用' }}
                  </el-tag>
                </span>
              </div>
            </div>
          </div>
          <div class="category-actions">
            <el-button type="primary" link @click="openEditDialog(category)">
              <el-icon><Edit /></el-icon> 编辑
            </el-button>
            <el-popconfirm title="确定删除该分类吗？" @confirm="handleDelete(category.id)">
              <template #reference>
                <el-button type="danger" link>
                  <el-icon><Delete /></el-icon> 删除
                </el-button>
              </template>
            </el-popconfirm>
          </div>
        </div>
        
        <div v-if="categories.length === 0 && !loading" class="empty-state">
          <el-empty description="暂无分类，点击右上角新增" />
        </div>
      </div>
      
      <!-- 新增/编辑弹窗 -->
      <el-dialog v-model="dialogVisible" :title="isEditMode ? '编辑分类' : '新增分类'" width="500px">
        <el-form :model="formData" :rules="formRules" ref="formRef" label-width="80px">
          <el-form-item label="分类名称" prop="category_name" required>
            <el-input v-model="formData.category_name" placeholder="请输入分类名称" />
          </el-form-item>
          <el-form-item label="排序">
            <el-input-number v-model="formData.sort_order" :min="0" :max="999" />
          </el-form-item>
          <el-form-item label="状态">
            <el-switch v-model="formData.status" :active-value="1" :inactive-value="0" active-text="启用" inactive-text="禁用" />
          </el-form-item>
        </el-form>
        <template #footer>
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitForm" :loading="submitting">确 定</el-button>
        </template>
      </el-dialog>
    </div>
  </template>
  
  <script>
  import { ref, reactive, onMounted } from 'vue'
  import { ElMessage, ElLoading } from 'element-plus'
  import Cookies from 'js-cookie'
  import axios from 'axios'
  import config from '@/config'
  import { Plus, Edit, Delete, Folder } from '@element-plus/icons-vue'
  
  export default {
    name: 'ProductCategoryPage',
    components: { Plus, Edit, Delete, Folder },
    setup() {
      const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
      const agentId = ref(userInfo.agent_id || 0)
      const storeId = ref(userInfo.id)
      
      const loading = ref(false)
      const submitting = ref(false)
      const dialogVisible = ref(false)
      const isEditMode = ref(false)
      const categories = ref([])
      const formRef = ref(null)
      
      const formData = reactive({
        id: null,
        category_name: '',
        sort_order: 0,
        status: 1
      })
      
      const formRules = {
        category_name: [{ required: true, message: '请输入分类名称', trigger: 'blur' }]
      }
      
      // 获取分类列表
      const fetchCategories = async () => {
        loading.value = true
        try {
          const response = await axios.get(`${config.BASE_URL}/shop/api/category_api.php`, {
            params: { agent_id: agentId.value, store_id: storeId.value, type: 'product' }
          })
          if (response.data.code === 200) {
            categories.value = response.data.data
          }
        } catch (error) {
          console.error('获取分类失败:', error)
          ElMessage.error('获取分类失败')
        } finally {
          loading.value = false
        }
      }
      
      // 打开新增弹窗
      const openAddDialog = () => {
        isEditMode.value = false
        Object.assign(formData, {
          id: null,
          category_name: '',
          sort_order: 0,
          status: 1
        })
        dialogVisible.value = true
      }
      
      // 打开编辑弹窗
      const openEditDialog = (category) => {
        isEditMode.value = true
        Object.assign(formData, {
          id: category.id,
          category_name: category.category_name,
          sort_order: category.sort_order,
          status: category.status
        })
        dialogVisible.value = true
      }
      
      // 提交表单
      const submitForm = async () => {
        if (!formRef.value) return
        
        await formRef.value.validate(async (valid) => {
          if (!valid) return
          
          submitting.value = true
          const loadingInstance = ElLoading.service({ text: '保存中...' })
          
          try {
            const payload = {
              agent_id: agentId.value,
              store_id: storeId.value,
              category_name: formData.category_name,
              sort_order: formData.sort_order,
              status: formData.status,
              type: 'product'
            }
            
            let response
            if (isEditMode.value) {
              payload.id = formData.id
              response = await axios.put(`${config.BASE_URL}/shop/api/category_api.php`, payload)
            } else {
              response = await axios.post(`${config.BASE_URL}/shop/api/category_api.php`, payload)
            }
            
            if (response.data.code === 200) {
              ElMessage.success(isEditMode.value ? '更新成功' : '新增成功')
              dialogVisible.value = false
              fetchCategories()
            } else {
              ElMessage.error(response.data.message || '操作失败')
            }
          } catch (error) {
            console.error('提交失败:', error)
            ElMessage.error('操作失败，请稍后重试')
          } finally {
            submitting.value = false
            loadingInstance.close()
          }
        })
      }
      
      // 删除分类
      const handleDelete = async (id) => {
        const loadingInstance = ElLoading.service({ text: '删除中...' })
        try {
          const response = await axios.delete(`${config.BASE_URL}/shop/api/category_api.php`, {
            data: { id, store_id: storeId.value }
          })
          if (response.data.code === 200) {
            ElMessage.success('删除成功')
            fetchCategories()
          } else {
            ElMessage.error(response.data.message || '删除失败')
          }
        } catch (error) {
          console.error('删除失败:', error)
          ElMessage.error('删除失败，请稍后重试')
        } finally {
          loadingInstance.close()
        }
      }
      
      onMounted(() => {
        fetchCategories()
      })
      
      return {
        loading,
        submitting,
        dialogVisible,
        isEditMode,
        categories,
        formData,
        formRef,
        formRules,
        openAddDialog,
        openEditDialog,
        submitForm,
        handleDelete
      }
    }
  }
  </script>
  
  <style scoped lang="scss">
  .product-category {
    .page-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
      
      .page-title {
        font-size: 24px;
        font-weight: 600;
        color: #1a1a2e;
        margin-bottom: 4px;
      }
      
      .page-desc {
        font-size: 14px;
        color: #8c8c8c;
      }
    }
    
    .category-list {
      display: flex;
      flex-direction: column;
      gap: 12px;
      
      .category-item {
        background: #fff;
        border-radius: 12px;
        padding: 16px 20px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        transition: all 0.3s;
        
        &:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
        
        .category-info {
          display: flex;
          align-items: center;
          gap: 16px;
          
          .category-icon {
            width: 48px;
            height: 48px;
            background: #e8f3ff;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          .category-detail {
            .category-name {
              font-size: 16px;
              font-weight: 500;
              color: #1a1a2e;
              margin-bottom: 6px;
            }
            
            .category-meta {
              display: flex;
              gap: 16px;
              font-size: 12px;
              color: #8c8c8c;
            }
          }
        }
        
        .category-actions {
          display: flex;
          gap: 12px;
        }
      }
    }
    
    
    .empty-state {
      padding: 60px 0;
    }
  }
  </style>